import { combineReducers, compose, createSelector } from '@reduxjs/toolkit';

import canvases from 'domains/canvases';
import products from 'domains/products';

import { hasFullyConfiguredCanvas, notNull } from 'utils/type_guards';

import slice from './saved_arrangements.slice';
import arrangementsAdapter from './saved_arrangements.adapter';

/** Represents the slice as it exists in the final store's reducer */
const combined = combineReducers({ [slice.name]: slice.reducer });
type GlobalState = ReturnType<typeof combined>;

/** Get the local state for the current domain */
const localState = (state: GlobalState) => state[slice.name];
type LocalState = ReturnType<typeof localState>;

const selectActiveSavedArrangementId = (state: LocalState) => state.active;

const selectActiveSavedArrangement = (state: LocalState) => {
  if (state.active === null) return;

  return state.entities[state.active];
};

const selectUnavailableArrangement = (state: LocalState) => state.unavailable;

const selectUnavailableArrangementIds = (state: LocalState) => state.unavailableIds;

const selectArrangementForSave = createSelector(
  [canvases.selectors.selectAll, products.selectors.selectEntities],
  (allCanvases, productsById) => {
    return allCanvases
      .map((canvas) => {
        const product = canvas.product ? productsById[canvas.product] : null;

        if (!product || !hasFullyConfiguredCanvas(canvas)) return null;

        const { x, y } = canvas.position;
        const { width, height } = products.utils.calculateProductSize(product, canvas);

        return { x, y, width, height, config: canvas };
      })
      .filter(notNull);
  }
);

export default {
  ...arrangementsAdapter.getSelectors(localState),
  activeId: compose(selectActiveSavedArrangementId, localState),
  active: compose(selectActiveSavedArrangement, localState),
  unavailable: compose(selectUnavailableArrangement, localState),
  unavailableIds: compose(selectUnavailableArrangementIds, localState),
  // Already globalized
  forSave: selectArrangementForSave,
};
